import React, { useState, useEffect } from 'react'
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  Snackbar,
  Button 
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Error as ErrorIcon } from '@material-ui/icons'
import CountUp from 'react-countup'
import ProduitVenduService from '../../services/ProduitVenduService';
import FondService from '../../services/FondService'; // Import du service des fonds/dépenses

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: theme.shadows[4]
    }
  },
  todayCard: {
    borderLeft: '4px solid #4CAF50'
  },
  yesterdayCard: {
    borderLeft: '4px solid #FF9800'
  },
  monthCard: {
    borderLeft: '4px solid #2196F3'
  },
  yearCard: {
    borderLeft: '4px solid #9C27B0'
  },
  depenseCard: {
    borderLeft: '4px solid #F44336'
  },
  modeItem: {
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius
  },
  depenseItem: {
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
    backgroundColor: '#ffebee',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #ffcdd2'
  },
  totalAmount: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    fontSize: '1.1rem'
  },
  depenseAmount: {
    fontWeight: 'bold',
    color: '#F44336',
    fontSize: '1.1rem'
  },
  netAmount: {
    fontWeight: 'bold',
    color: '#FF9800',
    fontSize: '1.1rem'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px'
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    backgroundColor: '#ffebee',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #f44336'
  },
  errorIcon: {
    marginRight: theme.spacing(1),
    color: '#f44336'
  },
  snackbarContent: {
    backgroundColor: '#f44336',
    color: 'white',
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center'
  },
  montant: {
    fontWeight: 'bold',
    color: '#2E7D32'
  },
  totalContainer: {
    marginTop: '15px', 
    padding: '10px', 
    backgroundColor: '#e8f5e8', 
    borderRadius: '4px',
    border: '1px solid #4CAF50'
  },
  depenseContainer: {
    marginTop: '10px', 
    padding: '10px', 
    backgroundColor: '#ffebee', 
    borderRadius: '4px',
    border: '1px solid #F44336'
  },
  netContainer: {
    marginTop: '10px', 
    padding: '10px', 
    backgroundColor: '#fff3e0', 
    borderRadius: '4px',
    border: '1px solid #FF9800'
  },
  separator: {
    margin: theme.spacing(2, 0),
    borderBottom: '2px dashed #e0e0e0'
  }
}));

function StatsModePaiement() {
  const classes = useStyles();
  const [stats, setStats] = useState({
    aujourdhui: [],
    hier: [],
    mois_courant: [],
    annee_courante: []
  });
  const [depenses, setDepenses] = useState({
    aujourdhui: 0,
    hier: 0,
    mois_courant: 0,
    annee_courante: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const periodes = [
    { key: 'aujourdhui', title: "Aujourd'hui", className: classes.todayCard },
    { key: 'hier', title: 'Hier', className: classes.yesterdayCard },
    { key: 'mois_courant', title: 'Mois en cours', className: classes.monthCard },
    { key: 'annee_courante', title: 'Année en cours', className: classes.yearCard }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('🔄 Chargement des statistiques...');
        
        // Charger les statistiques des ventes
        const response = await ProduitVenduService.getStatsSynthese();
        setStats(response.data);
        
        // Charger les dépenses
        await fetchDepenses();
        
        setError(null);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des stats:', error);
        setError('Erreur lors du chargement des statistiques');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fonction pour récupérer les dépenses depuis l'API
  const fetchDepenses = async () => {
    try {
      console.log('📊 Chargement des dépenses...');
      
      // Appeler les 4 endpoints pour les dépenses
      const [depensesAujourdhui, depensesHier, depensesMois, depensesAnnee] = await Promise.all([
        FondService.depensesJournee(),
        FondService.depensesHier(),
        FondService.depensesMonth(),
        FondService.depensesYear()
      ]);

      // Extraire les montants des réponses
      // Note: Adaptez cette partie selon la structure de votre réponse API
      const montantAujourdhui = depensesAujourdhui.data?.montant_total || 
                               depensesAujourdhui.data?.total || 
                               (Array.isArray(depensesAujourdhui.data) ? 
                                 depensesAujourdhui.data.reduce((sum, item) => sum + (item.montant || 0), 0) : 0);
      
      const montantHier = depensesHier.data?.montant_total || 
                         depensesHier.data?.total || 
                         (Array.isArray(depensesHier.data) ? 
                           depensesHier.data.reduce((sum, item) => sum + (item.montant || 0), 0) : 0);
      
      const montantMois = depensesMois.data?.montant_total || 
                         depensesMois.data?.total || 
                         (Array.isArray(depensesMois.data) ? 
                           depensesMois.data.reduce((sum, item) => sum + (item.montant || 0), 0) : 0);
      
      const montantAnnee = depensesAnnee.data?.montant_total || 
                          depensesAnnee.data?.total || 
                          (Array.isArray(depensesAnnee.data) ? 
                            depensesAnnee.data.reduce((sum, item) => sum + (item.montant || 0), 0) : 0);

      console.log('💰 Dépenses chargées:', {
        aujourdhui: montantAujourdhui,
        hier: montantHier,
        mois: montantMois,
        annee: montantAnnee
      });

      setDepenses({
        aujourdhui: montantAujourdhui,
        hier: montantHier,
        mois_courant: montantMois,
        annee_courante: montantAnnee
      });
      
    } catch (error) {
      console.error('❌ Erreur lors du chargement des dépenses:', error);
      // En cas d'erreur, on initialise à 0
      setDepenses({
        aujourdhui: 0,
        hier: 0,
        mois_courant: 0,
        annee_courante: 0
      });
    }
  };

  // Fonction pour calculer le total des ventes d'une période
  const calculerTotalVentes = (periodeKey) => {
    if (!stats[periodeKey] || stats[periodeKey].length === 0) return 0;
    
    return stats[periodeKey].reduce((total, item) => {
      return total + (Number(item.montant_total) || 0);
    }, 0);
  };

  // Fonction pour calculer le net (ventes - dépenses)
  const calculerNet = (periodeKey) => {
    const totalVentes = calculerTotalVentes(periodeKey);
    const totalDepenses = depenses[periodeKey] || 0;
    return totalVentes - totalDepenses;
  };

  // Composant pour formater les montants avec CountUp
  const FormattedMontant = ({ montant, className = '' }) => {
    return (
      <CountUp 
        start={0} 
        end={montant || 0} 
        duration={1.5} 
        separator=" " 
        suffix=" F CFA"
        className={className}
      />
    );
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    // Réessayer la requête
    Promise.all([
      ProduitVenduService.getStatsSynthese(),
      fetchDepenses()
    ])
      .then(([ventesResponse]) => {
        setStats(ventesResponse.data);
        setError(null);
      })
      .catch(error => {
        console.error('Erreur lors du rechargement:', error);
        setError('Erreur lors du chargement des statistiques');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h5" gutterBottom style={{ marginBottom: '30px', color: 'gray' }}>
        Synthèse par mode de paiement avec dépenses
      </Typography>
      
      {error && (
        <div className={classes.errorContainer}>
          <ErrorIcon className={classes.errorIcon} />
          <Typography variant="body1" style={{ flex: 1 }}>
            {error}
          </Typography>
          <Button color="secondary" onClick={handleRetry}>
            Réessayer
          </Button>
        </div>
      )}

      <Grid container spacing={3}>
        {periodes.map((periode) => {
          const totalVentes = calculerTotalVentes(periode.key);
          const totalDepenses = depenses[periode.key] || 0;
          const net = calculerNet(periode.key);
          
          return (
            <Grid item xs={12} md={6} lg={3} key={periode.key}>
              <Card className={`${classes.card} ${periode.className}`}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {periode.title}
                  </Typography>
                  
                  {stats[periode.key] && stats[periode.key].length > 0 ? (
                    <>
                      {/* Section Ventes par mode de paiement */}
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '10px', color: '#4CAF50' }}>
                        VENTES
                      </Typography>
                      
                      {stats[periode.key].map((item, index) => (
                        <div key={index} className={classes.modeItem}>
                          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                            {item.mode_payement || 'Non spécifié'}
                          </Typography>
                          <Grid container justifyContent="space-between">
                            <Typography variant="body2">
                              {item.nombre_ventes || 0} vente(s)
                            </Typography>
                            <Typography variant="body2" className={classes.totalAmount}>
                              <FormattedMontant montant={item.montant_total} />
                            </Typography>
                          </Grid>
                        </div>
                      ))}
                      
                      {/* Total Ventes */}
                      <div className={classes.totalContainer}>
                        <Grid container justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                            Total Ventes
                          </Typography>
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#2e7d32' }}>
                            <FormattedMontant montant={totalVentes} />
                          </Typography>
                        </Grid>
                      </div>

                      {/* Séparateur */}
                      <div className={classes.separator}></div>

                      {/* Section Dépenses */}
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '10px', color: '#F44336' }}>
                        DÉPENSES
                      </Typography>
                      
                      <div className={classes.depenseItem}>
                        <Grid container justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                            Total Dépenses
                          </Typography>
                          <Typography variant="subtitle2" className={classes.depenseAmount}>
                            <FormattedMontant montant={totalDepenses} />
                          </Typography>
                        </Grid>
                      </div>

                      {/* Section Résultat Net */}
                      <div className={classes.netContainer}>
                        <Grid container justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                            Résultat Net
                          </Typography>
                          <Typography 
                            variant="subtitle1" 
                            className={classes.netAmount}
                            style={{ 
                              color: net >= 0 ? '#2e7d32' : '#f44336' 
                            }}
                          >
                            <FormattedMontant montant={net} />
                          </Typography>
                        </Grid>
                        <Typography 
                          variant="body2" 
                          style={{ 
                            textAlign: 'center',
                            color: net >= 0 ? '#2e7d32' : '#f44336',
                            fontWeight: 'bold',
                            marginTop: '5px'
                          }}
                        >
                          {net >= 0 ? '✓ Bénéfice' : '✗ Perte'}
                        </Typography>
                      </div>
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: '20px' }}>
                      Aucune vente enregistrée
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Résumé global avec dépenses */}
      <Card style={{ marginTop: '30px', backgroundColor: '#f5f5f5' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Résumé Global avec Dépenses
          </Typography>
          <Grid container spacing={2}>
            {periodes.map((periode) => {
              const totalVentes = calculerTotalVentes(periode.key);
              const totalDepenses = depenses[periode.key] || 0; // CORRECTION ICI : periode.key au lieu de periodeKey
              const net = calculerNet(periode.key);
              const nombreVentes = stats[periode.key] ?
                stats[periode.key].reduce((sum, item) => sum + (item.nombre_ventes || 0), 0) : 0;
              
              return (
                <Grid item xs={12} sm={6} md={3} key={periode.key}>
                  <div style={{ textAlign: 'center', padding: '10px' }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {periode.title}
                    </Typography>
                    
                    {/* Ventes */}
                    <div style={{ marginBottom: '8px' }}>
                      <Typography variant="body2" color="textSecondary">
                        Ventes
                      </Typography>
                      <Typography variant="h6" style={{ color: '#4CAF50', fontWeight: 'bold' }}>
                        <FormattedMontant montant={totalVentes} />
                      </Typography>
                    </div>
                    
                    {/* Dépenses */}
                    <div style={{ marginBottom: '8px' }}>
                      <Typography variant="body2" color="textSecondary">
                        Dépenses
                      </Typography>
                      <Typography variant="body1" style={{ color: '#F44336', fontWeight: 'bold' }}>
                        <FormattedMontant montant={totalDepenses} />
                      </Typography>
                    </div>
                    
                    {/* Net */}
                    <div style={{ 
                      padding: '5px', 
                      backgroundColor: net >= 0 ? '#e8f5e8' : '#ffebee',
                      borderRadius: '4px'
                    }}>
                      <Typography variant="body2" color="textSecondary">
                        Net
                      </Typography>
                      <Typography variant="h6" style={{ 
                        color: net >= 0 ? '#2E7D32' : '#F44336', 
                        fontWeight: 'bold' 
                      }}>
                        <FormattedMontant montant={net} />
                      </Typography>
                    </div>
                    
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: '5px' }}>
                      {nombreVentes} vente(s)
                    </Typography>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Snackbar pour les erreurs */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={error}
        action={
          <Button color="inherit" size="small" onClick={handleCloseSnackbar}>
            Fermer
          </Button>
        }
      />
    </div>
  );
}

export default StatsModePaiement;